import { normalizeCountryCode } from "./baasEntidadeCreateVisibility";

/** Fallback legal types when country has no dedicated mapping. */
export const BAAS_DEFAULT_LEGAL_TYPES = [
  "llc",
  "corporation",
  "partnership",
  "sole_proprietorship",
  "non_profit",
];

/** Legal entity types available per country of incorporation. */
export const BAAS_COMPANY_TYPES_BY_COUNTRY = {
  US: ["llc", "corporation", "partnership", "sole_proprietorship", "non_profit"],
  BR: ["ltda", "sa", "mei", "eireli", "sociedade_simples", "cooperativa"],
  GB: ["ltd", "plc", "llp", "sole_trader", "partnership"],
  DE: ["gmbh", "ag", "ug", "einzelunternehmen", "gbr"],
  FR: ["sarl", "sa_fr", "sas", "ei", "sci"],
  ES: ["sl", "sa_es", "autonomo", "cooperativa_es"],
  PT: ["lda", "sa_pt", "eni", "cooperativa_pt"],
  CA: ["corporation", "llc", "sole_proprietorship", "partnership"],
  MX: ["sa_de_cv", "srl", "sole_proprietorship"],
  AE: ["llc", "fz_llc", "branch", "sole_establishment"],
};

const EU_SEPA_COUNTRIES = new Set(["DE", "FR", "ES", "PT"]);

export const BAAS_PAYMENT_RAIL_OPTIONS = ["ach", "wire", "swift", "sepa", "pix", "ted"];

/** Rails accepted by BaaS `PaymentRail` (TED is console-only and is dropped on save). */
export const BAAS_API_PAYMENT_RAILS = ["pix", "boleto", "ach", "wire", "sepa", "swift", "rtp"];

/**
 * Returns legal types valid for the selected country.
 * @param {string} countryCode
 */
export const getLegalTypesForCountry = (countryCode) => {
  const code = normalizeCountryCode(countryCode);
  if (!code) {
    return BAAS_DEFAULT_LEGAL_TYPES;
  }
  return BAAS_COMPANY_TYPES_BY_COUNTRY[code] ?? BAAS_DEFAULT_LEGAL_TYPES;
};

/**
 * @param {string} countryCode
 */
export const getDefaultLegalTypeForCountry = (countryCode) => {
  const types = getLegalTypesForCountry(countryCode);
  return types[0] ?? "llc";
};

/**
 * Returns payment rails applicable to the country of incorporation.
 * @param {string} countryCode
 */
export const getPaymentRailsForCountry = (countryCode) => {
  const code = normalizeCountryCode(countryCode);
  const rails = new Set(["wire", "swift"]);

  if (code === "US" || code === "CA") {
    rails.add("ach");
  }
  if (code === "BR") {
    rails.add("pix");
    rails.add("ted");
  }
  if (EU_SEPA_COUNTRIES.has(code) || code === "GB") {
    rails.add("sepa");
  }

  return BAAS_PAYMENT_RAIL_OPTIONS.filter((rail) => rails.has(rail));
};

/**
 * Normalizes rails from form state (array or legacy comma-separated string).
 * @param {string[] | string} value
 */
export const normalizePaymentRails = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim().toLowerCase()).filter(Boolean);
  }
  if (!value || typeof value !== "string") {
    return [];
  }
  return value
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
};

/**
 * Serializes rails for API payload (comma-separated).
 * @param {string[] | string} value
 */
export const serializePaymentRails = (value) => {
  const rails = normalizePaymentRails(value);
  return rails.length > 0 ? rails.join(",") : "";
};
